import { cn } from '@repo/ui';
import { type ReactElement, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/**
 * AppShell — the chrome around protected routes.
 *
 * Responsive rules (mobile-first):
 *   - <lg: top bar visible, hamburger toggles a slide-in drawer.
 *          Body scroll is locked while the drawer is open.
 *   - ≥lg: sidebar inline, drawer hidden.
 *
 * Drawer auto-closes on route change. We key the effect on
 * `location.pathname` (NOT `location.key`) because key changes on
 * query-string edits too — only path changes should hide the drawer.
 *
 * The drawer uses an opaque scrim (`bg-black/50`) and slides the
 * panel in 200 ms via `transition-transform`. We keep the panel a
 * fixed width that matches the desktop sidebar (w-52) so the layout
 * is coherent if the user resizes the window mid-session.
 */
export function AppShell(): ReactElement {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close the drawer on any navigation (path-only — query changes
  // keep the drawer as-is so the user can interact with filtered
  // lists without re-opening the menu).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only on path change; setMobileOpen is stable
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Body scroll lock while the drawer is open. Restoration uses the
  // previous `overflow` value so we don't clobber styles set elsewhere.
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // ESC closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-background text-foreground">
      {/* Desktop sidebar — fixed inline */}
      <Sidebar />

      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 lg:hidden',
          mobileOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          aria-label="Cerrar menú"
          tabIndex={mobileOpen ? 0 : -1}
          className={cn(
            'absolute inset-0 bg-black/50 transition-opacity duration-200',
            mobileOpen ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 flex w-52 max-w-[80vw] flex-col border-r border-border bg-card shadow-xl transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <Sidebar />
        </div>
      </div>

      <div className="flex h-full min-w-0 flex-1 flex-col">
        <TopBar onOpenSidebar={() => setMobileOpen(true)} isMobileSidebarOpen={mobileOpen} />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
